import {
    Box,
    Grid,
    Heading,
    Text,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    StatArrow,
    Card,
    CardBody,
    CardHeader,
    Flex,
    Icon,
    Badge,
    VStack,
    HStack,
    Spinner,
    Center,
    Button,
    IconButton,
    useColorModeValue,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Progress,
    Divider,
} from '@chakra-ui/react';
import {
    MdAttachMoney,
    MdTrendingUp,
    MdRefresh,
    MdPending,
    MdTimer,
    MdArrowForward,
    MdHistory,
    MdAssignment,
    MdWarning,
    MdCheckCircle,
    MdPendingActions,
    MdNotificationsActive,
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';

import {
    useDashboardStats,
    useImportedItems,
    usePriceSets,
    useProjects,
    usePipelineStats,
    usePursuitDashboard
} from '@/services/hooks';

interface StatCardProps {
    icon: React.ElementType;
    label: string;
    value: string | number;
    change?: string;
    changeType?: 'increase' | 'decrease';
    onClick?: () => void;
}

function StatCard({ icon, label, value, change, changeType, onClick }: StatCardProps) {
    const labelColor = useColorModeValue('gray.600', 'gray.400');

    return (
        <Card cursor={onClick ? 'pointer' : 'default'} onClick={onClick} _hover={onClick ? { transform: 'translateY(-2px)', shadow: 'xl' } : {}}>
            <CardBody>
                <Flex justify="space-between" align="flex-start">
                    <Stat>
                        <StatLabel color={labelColor} fontWeight="medium">{label}</StatLabel>
                        <StatNumber fontSize="2xl" mt={2}>
                            {value}
                        </StatNumber>
                        {change && (
                            <StatHelpText>
                                <StatArrow type={changeType || 'increase'} />
                                {change}
                            </StatHelpText>
                        )}
                    </Stat>
                    <Box p={3} bg="brand.500" borderRadius="lg" color="white">
                        <Icon as={icon} boxSize={6} />
                    </Box>
                </Flex>
            </CardBody>
        </Card>
    );
}

function ActiveOpportunitiesFeed() {
    const navigate = useNavigate();
    const { data, isLoading } = useProjects({ opportunity_type: 'active', pageSize: 5 });

    if (isLoading) return <Center p={4}><Spinner /></Center>;

    if (!data?.items?.length) {
        return <Center py={8}><Text color="gray.500">No active opportunities found.</Text></Center>;
    }

    return (
        <Table variant="simple" size="sm">
            <Thead>
                <Tr>
                    <Th>Project</Th>
                    <Th>Deadline</Th>
                    <Th>Type</Th>
                    <Th></Th>
                </Tr>
            </Thead>
            <Tbody>
                {data.items.map((p: any) => (
                    <Tr key={p.id} _hover={{ bg: 'whiteAlpha.50' }} cursor="pointer" onClick={() => navigate(`/bid-room/${p.id}`)}>
                        <Td fontWeight="medium" maxW="200px" isTruncated>{p.title}</Td>
                        <Td color="red.300">
                            {p.submission_deadline ? new Date(p.submission_deadline).toLocaleDateString() : 'N/A'}
                        </Td>
                        <Td>
                            <Badge colorScheme={p.opportunity_type === 'ita' ? 'brand' : 'green'}>
                                {p.opportunity_type?.toUpperCase() || 'BID'}
                            </Badge>
                        </Td>
                        <Td>
                            <IconButton aria-label="View" icon={<MdArrowForward />} size="xs" variant="ghost" />
                        </Td>
                    </Tr>
                ))}
            </Tbody>
        </Table>
    );
}

function ActivityFeed() {
    const { data, isLoading } = useImportedItems({
        type: 'active',
        status: 'imported',
        limit: 5,
        sort_by: 'submission_deadline',
        sort_order: 'desc'
    });

    if (isLoading) return <Center py={8}><Spinner color="brand.500" /></Center>;

    const items = data?.items || [];

    if (items.length === 0) return <Center py={8}><Text color="gray.500">No new imported bids</Text></Center>;

    return (
        <VStack spacing={4} align="stretch" px={4} pb={4}>
            {items.map((item: any) => (
                <Flex key={item.id} p={3} bg="whiteAlpha.50" borderRadius="lg" align="center" justify="space-between" border="1px solid" borderColor="whiteAlpha.100">
                    <HStack spacing={3}>
                        <Icon as={MdPending} color="yellow.400" />
                        <Box>
                            <Text fontSize="sm" fontWeight="medium" isTruncated maxW="250px">{item.description}</Text>
                            <Text fontSize="xs" color="gray.500">{item.agency} • {item.feed_name}</Text>
                        </Box>
                    </HStack>
                    <Badge colorScheme="yellow" variant="subtle">NEW</Badge>
                </Flex>
            ))}
        </VStack>
    );
}

// --- New War Room Components ---

function KanbanColumn({ title, stage, pursuits, navigate }: { title: string, stage: string, pursuits: any[], navigate: any }) {
    const bg = useColorModeValue('gray.50', 'gray.800');
    const border = useColorModeValue('gray.200', 'gray.700');
    const items = pursuits.filter((p: any) => p.stage === stage);

    return (
        <Box bg={bg} borderRadius="xl" p={4} minH="300px" borderWidth="1px" borderColor={border}>
            <Flex justify="space-between" align="center" mb={4}>
                <Heading size="sm" color="gray.500">{title}</Heading>
                <Badge borderRadius="full">{items.length}</Badge>
            </Flex>
            <VStack spacing={3} align="stretch">
                {items.length === 0 ? (
                    <Center py={8}>
                        <Text fontSize="sm" color="gray.400">No pursuits in this stage</Text>
                    </Center>
                ) : (
                    items.map((p: any) => <KanbanCard key={p.id} pursuit={p} navigate={navigate} />)
                )}
            </VStack>
        </Box>
    );
}

function KanbanCard({ pursuit, navigate }: { pursuit: any, navigate: any }) {
    const cardBg = useColorModeValue('white', 'gray.700');
    const cardBorder = useColorModeValue('gray.200', 'gray.600');

    // Status colors and labels
    const healthLabels: Record<string, { color: string, text: string }> = {
        green: { color: 'green', text: 'ON TRACK' },
        yellow: { color: 'yellow', text: 'AT RISK' },
        red: { color: 'red', text: 'URGENT' }
    };
    const healthData = healthLabels[pursuit.health] || { color: 'gray', text: 'UNKNOWN' };
    const healthColor = healthData.color;
    const healthText = healthData.text;

    const compProgress = pursuit.compliance_total > 0
        ? (pursuit.compliance_completed / pursuit.compliance_total) * 100
        : 0;

    return (
        <Box
            bg={cardBg}
            p={4}
            borderRadius="lg"
            borderWidth="1px"
            borderColor={cardBorder}
            borderLeftWidth="4px"
            borderLeftColor={`${healthColor}.400`}
            shadow="sm"
            cursor="pointer"
            onClick={() => navigate(`/bid-room/${pursuit.id}`)}
            _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
            transition="all 0.2s"
        >
            <HStack justify="space-between" mb={2}>
                <Badge colorScheme={healthColor} variant="subtle" fontSize="2xs">
                    {healthText}
                </Badge>
                {pursuit.due_date && (
                    <Text fontSize="xs" color="gray.500" fontWeight="bold">
                        Due: {new Date(pursuit.due_date).toLocaleDateString()}
                    </Text>
                )}
            </HStack>

            <Heading size="xs" noOfLines={2} mb={3} lineHeight="tall">
                {pursuit.imported_item?.project_title || pursuit.imported_item?.description || "Unnamed Pursuit"}
            </Heading>

            <Box mb={2}>
                <HStack justify="space-between" mb={1}>
                    <Text fontSize="xs" color="gray.500">Requirements Check</Text>
                    <Text fontSize="xs" fontWeight="bold">{pursuit.compliance_completed}/{pursuit.compliance_total}</Text>
                </HStack>
                <Progress value={compProgress} size="xs" colorScheme="blue" borderRadius="full" />
            </Box>

            <Box>
                <HStack justify="space-between" mb={1}>
                    <Text fontSize="xs" color="gray.500">Submittals Assembled</Text>
                    <Text fontSize="xs" fontWeight="bold">{pursuit.submittals_completed}/{pursuit.submittals_total}</Text>
                </HStack>
            </Box>

            <Divider my={3} />

            <HStack color={`${healthColor}.600`} _dark={{ color: `${healthColor}.300` }}>
                <Icon as={healthColor === 'red' ? MdWarning : MdPendingActions} />
                <Text fontSize="xs" fontWeight="medium" isTruncated>
                    {pursuit.next_action}
                </Text>
            </HStack>
        </Box>
    );
}

function UrgencyFeed({ pursuits, navigate }: { pursuits: any[], navigate: any }) {
    const urgent = pursuits
        .filter(p => p.due_date && p.health !== 'green')
        .sort((a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime())
        .slice(0, 5);

    if (urgent.length === 0) {
        return (
            <Center py={6}>
                <VStack>
                    <Icon as={MdCheckCircle} boxSize={8} color="green.400" mb={1} />
                    <Text color="gray.500" fontSize="sm">No urgent pursuit actions.</Text>
                </VStack>
            </Center>
        );
    }

    return (
        <VStack spacing={3} align="stretch" px={4} pb={4}>
            {urgent.map((p: any) => (
                <Flex
                    key={p.id}
                    p={3}
                    bg={p.health === 'red' ? 'red.50' : 'orange.50'}
                    _dark={{ bg: p.health === 'red' ? 'whiteAlpha.100' : 'whiteAlpha.50' }}
                    borderRadius="lg"
                    align="center"
                    justify="space-between"
                    cursor="pointer"
                    onClick={() => navigate(`/bid-room/${p.id}`)}
                    _hover={{ shadow: 'sm' }}
                >
                    <HStack spacing={3}>
                        <Icon as={p.health === 'red' ? MdWarning : MdPendingActions} color={p.health === 'red' ? 'red.500' : 'orange.500'} />
                        <Box>
                            <Text fontSize="sm" fontWeight="bold" noOfLines={1} maxW="200px">
                                {p.imported_item?.project_title || "Unnamed Bid"}
                            </Text>
                            <Text fontSize="xs" color={p.health === 'red' ? 'red.600' : 'orange.600'} _dark={{ color: p.health === 'red' ? 'red.300' : 'orange.300' }}>
                                {p.next_action}
                            </Text>
                        </Box>
                    </HStack>
                    <IconButton aria-label="Go" icon={<MdArrowForward />} size="xs" variant="ghost" colorScheme={p.health === 'red' ? 'red' : 'orange'} />
                </Flex>
            ))}
        </VStack>
    );
}

// --- Main Page ---

export function Dashboard() {
    const navigate = useNavigate();
    const { data: stats, isLoading: statsLoading, refetch } = useDashboardStats();
    const { data: priceSets } = usePriceSets();
    const { data: pipelineStats } = usePipelineStats();
    const { data: pursuits, isLoading: pursuitsLoading } = usePursuitDashboard();

    return (
        <Box>
            <Flex justify="space-between" align="center" mb={6}>
                <Heading size="lg">Command Center</Heading>
                <Button leftIcon={<MdRefresh />} variant="ghost" size="sm" onClick={() => refetch()} isLoading={statsLoading}>Refresh</Button>
            </Flex>

            {/* Original Stat Cards */}
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={6} mb={8}>
                <StatCard
                    icon={MdTimer}
                    label="Active Pursuits"
                    value={pipelineStats?.total_active || 0}
                    change={pipelineStats?.closing_soon ? `${pipelineStats.closing_soon} closing soon` : 'Pipeline healthy'}
                    onClick={() => navigate('/pursuits')}
                />
                <StatCard
                    icon={MdHistory}
                    label="Historical Items"
                    value={stats?.totalProjects || 0}
                    change="Analysis ready"
                    onClick={() => navigate('/imported')}
                />
                <StatCard
                    icon={MdAttachMoney}
                    label="Intelligence"
                    value={priceSets?.length || 0}
                    change="Price sets"
                    onClick={() => navigate('/price-sets')}
                />
                <StatCard
                    icon={MdTrendingUp}
                    label="New Imported Bids"
                    value={stats?.pendingBatches || 0}
                    onClick={() => navigate('/imported')}
                />
            </Grid>

            {/* NEW: Pursuit War Room Kanban */}
            <Box mb={8}>
                <Flex justify="space-between" align="flex-end" mb={4}>
                    <Box>
                        <Heading size="md" mb={1}>Bid Pursuit Pipeline</Heading>
                        <Text color="gray.500" fontSize="sm">Track active bids, compliance progress, and required submittals.</Text>
                    </Box>
                    <Button leftIcon={<MdAssignment />} colorScheme="brand" onClick={() => navigate('/pursuits')} size="sm">
                        View All
                    </Button>
                </Flex>
                {pursuitsLoading ? (
                    <Center py={10}><Spinner color="brand.500" /></Center>
                ) : (
                    <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={6} alignContent="start">
                        <KanbanColumn title="Reviewing & Planning" stage="reviewing" pursuits={pursuits || []} navigate={navigate} />
                        <KanbanColumn title="Drafting & Assembling" stage="drafting" pursuits={pursuits || []} navigate={navigate} />
                        <KanbanColumn title="Submitted (Awaiting Award)" stage="submitted" pursuits={pursuits || []} navigate={navigate} />
                    </Grid>
                )}
            </Box>

            {/* Feeds section (Side-by-side) */}
            <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={6} mb={8}>
                {/* Urgency Panel */}
                <Card height="100%">
                    <CardHeader pb={2}>
                        <HStack>
                            <Icon as={MdNotificationsActive} color="red.500" />
                            <Heading size="sm">Urgent Actions</Heading>
                        </HStack>
                    </CardHeader>
                    <CardBody pt={0} px={0} pb={2}>
                        <UrgencyFeed pursuits={pursuits || []} navigate={navigate} />
                    </CardBody>
                </Card>

                {/* Active Opportunities */}
                <Card height="100%">
                    <CardHeader pb={2}>
                        <Flex justify="space-between" align="center">
                            <Heading size="sm">Active Opportunities</Heading>
                            <Button size="xs" colorScheme="brand" variant="link" onClick={() => navigate('/pursuits')}>View</Button>
                        </Flex>
                    </CardHeader>
                    <CardBody pt={0} px={0}>
                        <ActiveOpportunitiesFeed />
                    </CardBody>
                </Card>
            </Grid>

            {/* Full width bottom card */}
            <Card>
                <CardHeader pb={2}>
                    <Flex justify="space-between" align="center">
                        <Heading size="sm">New Imported Bids Review</Heading>
                        <Button size="sm" variant="ghost" onClick={() => navigate('/imported')}>View All</Button>
                    </Flex>
                </CardHeader>
                <CardBody pt={0} px={0}>
                    <ActivityFeed />
                </CardBody>
            </Card>
        </Box>
    );
}
